import { EmployeeI } from '../domain/Employee';
import { IEmployee } from '../domain/NewEmployee';
import EmployeesRepositoryI, {
  CreateEmployeeReqI,
  UpdateEmployeeReqI,
} from './EmployeesRepositoryI';
import { FetcherAmplify } from 'src/business/Libraries/Fetcher/FetcherAmplify';
import { FetcherAxios } from 'src/business/Libraries/Fetcher/FetcherAxios';

export default class EmployeesRepositoryImpl implements EmployeesRepositoryI {
  private fetcherAmplify;
  private fetcherAxios;

  constructor(fetcherAmplify: FetcherAmplify, fetcherAxios: FetcherAxios) {
    this.fetcherAmplify = fetcherAmplify;
    this.fetcherAxios = fetcherAxios;
  }

  private async saveFile(url: string, fields: any, file: any) {
    const formData = new FormData();
    const keys = [
      'Content-Type',
      'key',
      'bucket',
      'X-Amz-Algorithm',
      'X-Amz-Credential',
      'X-Amz-Date',
      'X-Amz-Security-Token',
      'Policy',
      'X-Amz-Signature',
    ];
    keys.forEach((name) => {
      formData.append(name, fields[name]);
    });
    formData.append('file', file);
    return this.fetcherAxios.post(url, formData);
  }

  public async createEmployee({
    customerId,
    employee,
  }: {
    customerId: string;
    employee: CreateEmployeeReqI;
  }): Promise<void> {
    const response = await this.fetcherAmplify.post(
      `/v1/customers/${customerId}/employees`,
      {
        body: {
          ...employee,
        },
      },
    );

    return response;
  }

  public async getEmployees(customerId: string): Promise<
    (Omit<EmployeeI, 'roles'> & {
      roles: string[];
    })[]
  > {
    return this.fetcherAmplify.get(`/v1/customers/${customerId}/employees`);
  }

  public async getEmployee(
    customerId: string,
    employeeId: string,
  ): Promise<any> {
    return this.fetcherAmplify.get(
      `/v1/customers/${customerId}/employees/${employeeId}`,
    );
  }

  public async updateEmployee({
    customerId,
    employee,
    employeeId,
  }: {
    employeeId: string;
    customerId: string;
    employee: UpdateEmployeeReqI;
  }): Promise<void> {
    return this.fetcherAmplify.put(
      `/v1/customers/${customerId}/employees/${employeeId}`,
      {
        body: {
          ...employee,
        },
      },
    );
  }

  public async newGetEmployees({
    customerId,
  }: {
    customerId: string;
  }): Promise<Partial<IEmployee>[]> {
    return this.fetcherAmplify.get(`/v1/customers/${customerId}/employees`, {
      queryStringParameters: {
        fullData: true,
      },
    });
  }

  public async newCreateEmployee({
    customerId,
    employee,
    attachments,
  }: {
    customerId: string;
    employee: Partial<IEmployee>;
    attachments?: { documentoContacto?: File };
  }): Promise<any> {
    const response = await this.fetcherAmplify.post(
      `/v1/customers/${customerId}/employees`,
      {
        body: {
          ...employee,
        },
      },
    );

    // Si hay archivos y el backend devolvió URLs pre-firmadas, subirlos
    const filesToUpload: Function[] = [];
    if (
      response.attachments?.documentoContacto?.upload &&
      attachments?.documentoContacto
    ) {
      const { url, fields } = response.attachments.documentoContacto.upload;
      filesToUpload.push(() =>
        this.saveFile(url, fields, attachments.documentoContacto),
      );
    }

    await Promise.all(filesToUpload.map((f) => f()));

    return response;
  }

  public async newUpdateEmployee({
    customerId,
    employeeId,
    employee,
    attachments,
  }: {
    customerId: string;
    employeeId: string;
    employee: Partial<IEmployee>;
    attachments?: { documentoContacto?: File };
  }): Promise<any> {
    const response = await this.fetcherAmplify.put(
      `/v1/customers/${customerId}/employees/${employeeId}`,
      {
        body: {
          ...employee,
        },
      },
    );

    // Si hay archivos y el backend devolvió URLs pre-firmadas, subirlos
    const filesToUpload: Function[] = [];
    if (
      response.attachments?.documentoContacto?.upload &&
      attachments?.documentoContacto
    ) {
      const { url, fields } = response.attachments.documentoContacto.upload;
      filesToUpload.push(() =>
        this.saveFile(url, fields, attachments.documentoContacto),
      );
    }

    await Promise.all(filesToUpload.map((f) => f()));

    return response;
  }
}
